// @owners { team: patients }
/**
 * "Add a card" built on Stripe's Payment Element with Stripe Link, gated behind the
 * `stripe_payment_element_web` feature flag (legacy form: AddPaymentCard.web.tsx).
 *
 * Flow: fetch a SetupIntent client_secret from the backend → mount the Payment
 * Element against it → `stripe.confirmSetup` produces a PaymentMethod → hand the
 * SetupIntent id to the backend, which attaches the PaymentMethod to the customer.
 */
import { BASE_FONT_SIZE, BORDERS, COLORS, TYPOGRAPHY } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Column,
  Description,
  InlineAlert,
  LgSpacing,
  MdSpacing,
  Toast,
  ToastContext,
} from '@alto/design-system';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type * as stripeJs from '@stripe/stripe-js';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { type PaymentMethodType } from '~shared/constants';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useCreateStripeSetupIntent } from '~shared/queries/query-keys/paymentMethods';
import { useCreateCreditCardPaymentMethodFromSetupIntent } from '~shared/queries/usePaymentMethodMutations';
// Named distinctly from apps/web's own Inter-Regular.ttf (apps/web/src/stylesheets/fonts.scss) -
// webpack's asset filename template (`fonts/[name][ext]`, no content hash) maps output paths by
// source filename alone, so importing a second file that happens to share that basename collides
// with apps/web's copy in the bundle ("Multiple chunks emit assets to the same filename").
import interRegularFontUrl from '../assets/fonts/Inter-Regular-PaymentElement.ttf';
import { getStripeBusinessName } from '../helpers/getStripeBusinessName';
import { CARD_ALREADY_SAVED_ERROR, getCardSaveErrorMessage } from '../helpers/paymentErrors';
import { stripePromise } from '../helpers/stripePromise.web';

type Props = {
  readonly onClose: () => void;
  // Called when the Payment Element can't come up at all (SetupIntent creation
  // failed). Hosts use this to fall back to the legacy card form - a Stripe/backend
  // outage shouldn't dead-end the patient on an error. When provided, the inline
  // "Unable to start adding a card" error UI never shows.
  readonly onStripeUnavailable?: () => void;
  // pass payment method type when in checkout context, otherwise leave undefined
  // so the payment method is not set as the order's payment method
  readonly paymentMethodType?: PaymentMethodType;
};

// Style the Payment Element's inputs to match InputText as closely as Stripe's
// Appearance API allows. Stripe's Elements render inside a cross-origin iframe, so
// they don't inherit the page's @font-face rules (apps/web/src/stylesheets/fonts.scss)
// - the font has to be registered directly on `<Elements fonts={...}>` instead.
const paymentElementFonts: stripeJs.CustomFontSource[] = [
  { family: TYPOGRAPHY.FONT.BODY.REGULAR, src: `url(${interRegularFontUrl})`, weight: '400' },
];

const paymentElementAppearance: stripeJs.Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: COLORS.PALETTE.PRIMARY.DEFAULT,
    colorBackground: COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST,
    colorText: COLORS.TEXT_COLORS.BLACK,
    colorDanger: COLORS.TEXT_COLORS.DANGER,
    fontFamily: `'${TYPOGRAPHY.FONT.BODY.REGULAR}', sans-serif`,
    fontSizeBase: `${BASE_FONT_SIZE}px`,
    // Matches InputText's Border radius="LG".
    borderRadius: `${BORDERS.RADIUS.LG.value}px`,
  },
  rules: {
    '.Label': {
      color: COLORS.TEXT_COLORS.GREY,
    },
    '.Input': {
      borderColor: COLORS.BORDER_COLORS.LIGHT,
    },
    '.Input:focus': {
      borderColor: COLORS.BORDER_COLORS.SECONDARY,
    },
    '.Input::placeholder': {
      color: COLORS.TEXT_COLORS.DISABLED,
    },
  },
};

const AddPaymentCardPaymentElementForm = ({ onClose, onStripeUnavailable, paymentMethodType }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const { trackEvent } = useAnalytics();
  const entryPoint = paymentMethodType ? 'checkout' : 'payments';
  const { addToast } = useContext(ToastContext);
  const { mutateAsync: createCreditCardPaymentMethodFromSetupIntent } =
    useCreateCreditCardPaymentMethodFromSetupIntent();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!stripe || !elements || submitting) {
      return;
    }
    trackEvent({ event: EVENTS.ADD_CARD_TAPPED });
    setSubmitting(true);
    setSubmitError(null);

    // `redirect: 'if_required'` keeps us inline unless the card requires SCA/3DS.
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (error || !setupIntent) {
      setSubmitError(error?.message ?? 'Something went wrong saving your card.');
      setSubmitting(false);
      trackEvent({ event: EVENTS.CREATE_PAYMENT_METHOD_FAILED, params: { stripeLinkFormEnabled: true } });
      return;
    }

    try {
      await createCreditCardPaymentMethodFromSetupIntent({ setupIntentId: setupIntent.id, paymentMethodType });
      setSubmitting(false);
      onClose();
      addToast(<Toast variant="success">Successfully added credit card!</Toast>);
      trackEvent({
        event: EVENTS.PAYMENT_METHOD_CREATED,
        params: { stripeLinkFormEnabled: true, type: 'StripeData' },
      });
    } catch (serverError) {
      setSubmitting(false);
      const message = getCardSaveErrorMessage(serverError);
      if (message === CARD_ALREADY_SAVED_ERROR) {
        // Nothing to retry - the card is already on the account, so close the
        // form and surface the error as a toast instead of a dead-end inline alert.
        onClose();
        addToast(<Toast variant="error">{message}</Toast>);
      } else {
        setSubmitError(message);
      }
      trackEvent({ event: EVENTS.CREATE_PAYMENT_METHOD_FAILED, params: { stripeLinkFormEnabled: true } });
    }
  };

  return (
    <Column>
      {/*
        No LinkAuthenticationElement, and deliberately no defaultValues email:
        prefilling the email auto-logs returning Link users into Link with their
        saved card preselected, which made adding a new card confusing. Without it
        Link stays collapsed into its opt-in accordion and the form opens on the
        card fields (design decision from the staging review).
      */}
      <PaymentElement
        options={{
          layout: 'tabs',
          // Overrides the Stripe account's business name in the consent copy
          // ("you allow X to charge your card..."), which is brand-dependent.
          business: { name: getStripeBusinessName() },
        }}
        onLoadError={(event) => {
          // The element failed to mount (e.g. Stripe rejected the session), so this
          // form can never succeed - fall back to the legacy card form rather than
          // stranding the patient on a dead error.
          trackEvent({ event: EVENTS.CREATE_PAYMENT_METHOD_FAILED, params: { stripeLinkFormEnabled: true } });
          if (onStripeUnavailable) {
            onStripeUnavailable();
          } else {
            setSubmitError(event.error.message ?? 'Unable to start adding a card. Please try again.');
          }
        }}
        onReady={() => {
          // The iframe finished mounting - the patient can actually see the form now.
          trackEvent({ event: EVENTS.CARD_FORM_VIEWED, params: { stripeLinkFormEnabled: true, entryPoint } });
        }}
      />
      {submitError ? (
        <>
          <LgSpacing />
          <InlineAlert type="error">
            <Description>{submitError}</Description>
          </InlineAlert>
        </>
      ) : null}
      <LgSpacing />
      <Button
        key="add-card-submit"
        loading={submitting}
        disabled={!stripe || !elements || submitting}
        label="Add card"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onPress={handleSubmit}
      />
      <MdSpacing />
      <Button
        key="add-card-cancel-button"
        disabled={submitting}
        label="Cancel"
        onPress={() => {
          trackEvent({ event: EVENTS.CARD_FORM_DISMISSED, params: { stripeLinkFormEnabled: true, entryPoint } });
          onClose();
        }}
        type="tertiary"
      />
    </Column>
  );
};

export const AddPaymentCardPaymentElement = ({ onClose, onStripeUnavailable, paymentMethodType }: Props) => {
  const { clientSecret, error, isLoading } = useCreateStripeSetupIntent();

  // Memoized per Stripe's guidance: a fresh `options` object on every render makes
  // <Elements> diff/update on unrelated parent re-renders. Nothing here changes
  // after the client secret arrives (appearance/fonts are module constants).
  const options: stripeJs.StripeElementsOptions | null = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: paymentElementAppearance,
            fonts: paymentElementFonts,
          }
        : null,
    [clientSecret],
  );

  // SetupIntent creation failed - without a client secret the Payment Element can
  // never mount, so hand control back to the host's legacy-form fallback right away.
  useEffect(() => {
    if (error && onStripeUnavailable) {
      onStripeUnavailable();
    }
  }, [error, onStripeUnavailable]);

  if (error) {
    // With a fallback host, render nothing for the instant before the effect above
    // hands off - the dead-end error UI should never flash.
    if (onStripeUnavailable) {
      return null;
    }
    return (
      <Column>
        <InlineAlert type="error">
          <Description>Unable to start adding a card. Please try again.</Description>
        </InlineAlert>
        <LgSpacing />
        <Button
          key="add-card-error-cancel-button"
          label="Cancel"
          onPress={onClose}
          type="tertiary"
        />
      </Column>
    );
  }

  // The Payment Element cannot mount until the SetupIntent client_secret arrives.
  if (isLoading || !options) {
    return <Body>Loading secure payment form…</Body>;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={options}
    >
      <AddPaymentCardPaymentElementForm
        onClose={onClose}
        onStripeUnavailable={onStripeUnavailable}
        paymentMethodType={paymentMethodType}
      />
    </Elements>
  );
};
